import { useState, useMemo } from "react"
import { Button } from "@/components/ui/button"
import { ChevronDown, Check } from "lucide-react"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { cn } from "@/lib/utils"
import { useSelector } from "react-redux"
import { selectAllDriversAccountChats } from "@/reducers/accountChatsSelectors"
import { useTranslation } from "react-i18next"

interface DriverFilterProps {
  selectedChat: string
  onChatChange: (chatJid: string) => void
}

export function DriverFilter({
  selectedChat,
  onChatChange,
}: DriverFilterProps) {
  const { t } = useTranslation()
  const [driverPopoverOpen, setDriverPopoverOpen] = useState(false)
  const driverAccountChatsMap = useSelector(selectAllDriversAccountChats)

  // Get chats for the dropdown (sorted by name)
  const chatOptions = useMemo(() => {
    return Array.from(driverAccountChatsMap.values())
      .map((chat) => ({
        jid: chat.jid,
        name: chat.name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [driverAccountChatsMap])

  const selectedChatName = useMemo(() => {
    if (selectedChat === "all-drivers")
      return t("orders.report.driverFilter.allDrivers")
    return (
      chatOptions.find((chat) => chat.jid === selectedChat)?.name ||
      t("orders.report.driverFilter.allDrivers")
    )
  }, [selectedChat, chatOptions, t])

  const handleSelectChat = (chatJid: string) => {
    onChatChange(chatJid)
    setDriverPopoverOpen(false)
  }

  // Use searchable dropdown if more than 5 drivers
  if (chatOptions.length > 5) {
    return (
      <Popover open={driverPopoverOpen} onOpenChange={setDriverPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={driverPopoverOpen}
            className="w-[300px] justify-between"
          >
            <span className="truncate">{selectedChatName}</span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[300px] p-0">
          <Command>
            <CommandInput
              placeholder={t("orders.report.driverFilter.searchPlaceholder")}
            />
            <CommandList className="max-h-[300px] overflow-y-auto">
              <CommandEmpty>
                {t("orders.report.driverFilter.noDriverFound")}
              </CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value="all-drivers"
                  onSelect={() => handleSelectChat("all-drivers")}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      selectedChat === "all-drivers"
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                  {t("orders.report.driverFilter.allDrivers")}
                </CommandItem>
                {chatOptions.map((chat) => (
                  <CommandItem
                    key={chat.jid}
                    value={chat.name}
                    onSelect={() => handleSelectChat(chat.jid)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedChat === chat.jid ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {chat.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    )
  }

  // Use simple Select for 5 or fewer drivers
  return (
    <Select value={selectedChat} onValueChange={onChatChange}>
      <SelectTrigger className="w-[300px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all-drivers">
          {t("orders.report.driverFilter.allDrivers")}
        </SelectItem>
        {chatOptions.map((chat) => (
          <SelectItem key={chat.jid} value={chat.jid}>
            {chat.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
